import { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import Sidebar from "../../../../components/layout/Sidebar/Sidebar";
import { useCategories } from "../../../category/hooks/useCategories";
import { getUserInfo } from "../../../settings/api/accountApi";
import PostList from "../../components/PostList/PostList";
import { usePosts } from "../../hooks/usePosts";
import styles from "./UserBlogPage.module.css";

const UserBlogPage = () => {
  const { userId } = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState("all");

  const { categories: categoriesData } = useCategories(userId);

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
  };

  const getAllDescendantIds = useCallback((categoryId, catsData) => {
    const ids = [categoryId];

    const findCategory = (cats, id) => {
      for (const cat of cats) {
        if (cat.id === id) return cat;
        if (cat.children) {
          const found = findCategory(cat.children, id);
          if (found) return found;
        }
      }
      return null;
    };

    const category = findCategory(catsData, categoryId);
    if (category && category.children) {
      category.children.forEach((child) => {
        ids.push(...getAllDescendantIds(child.id, catsData));
      });
    }

    return ids;
  }, []);

  const categoryParam = useMemo(() => {
    if (selectedCategory === "all") return "all";
    if (selectedCategory === "uncategorized") return "uncategorized";
    return getAllDescendantIds(selectedCategory, categoriesData).join(",");
  }, [selectedCategory, categoriesData, getAllDescendantIds]);

  const { posts: pagedPostsData, loading } = usePosts({
    type: "author",
    authorId: userId,
    params: {
      page: currentPage - 1,
      category: categoryParam,
    },
  });

  const { posts: unpagedPostsData } = usePosts({
    type: "author-unpaged",
    authorId: userId,
  });

  const allPosts = unpagedPostsData || [];

  const [categories, setCategories] = useState([]);
  const [blogOwner, setBlogOwner] = useState(null);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await getUserInfo(userId);
        if (response.data.data?.profile) {
          setBlogOwner({
            id: response.data.id,
            nickname: response.data.data.profile.nickname,
            blogName: response.data.data.profile.blogName,
            avatar: response.data.data.profile.avatar
          });
        }
      } catch (error) {
        console.error("Failed to fetch user info:", error);
      }
    };

    if (userId) {
      fetchUserInfo();
    }
  }, [userId]);

  const flattenCategories = (cats, depth = 0, out = []) => {
    (cats || []).forEach((c) => {
      out.push({ ...c, depth });
      if (c.children && c.children.length > 0) {
        flattenCategories(c.children, depth + 1, out);
      }
    });
    return out;
  };

  useEffect(() => {
    const flatCategories = flattenCategories(categoriesData);

    const uncategorizedCount = allPosts.filter(post => !post.category?.id).length;

    const categoryList = [
      { id: "all", name: "전체", count: allPosts.length },
      ...flatCategories.map((cat) => {
        const catDescendantIds = getAllDescendantIds(cat.id, categoriesData);
        const count = allPosts.filter((post) =>
          post.category?.id && catDescendantIds.includes(post.category.id)
        ).length;

        return {
          id: cat.id,
          name: cat.name,
          depth: cat.depth,
          count: count,
          children: cat.children,
        };
      }),
    ];

    if (uncategorizedCount > 0) {
      categoryList.push({
        id: "uncategorized",
        name: "미분류",
        depth: 0,
        count: uncategorizedCount,
      });
    }

    setCategories(categoryList);
  }, [allPosts, categoriesData, getAllDescendantIds]);

  return (
    <div className={styles.userBlogPage}>
      <Sidebar
        categories={categories}
        onCategorySelect={handleCategorySelect}
        selectedCategory={selectedCategory}
        accountId={userId}
      />

      <div className={styles.mainContent}>
        <div className={styles.container}>
          <div className={styles.profileHeader}>
            <div className={styles.profileInfo}>
              <div className={styles.avatar}>
                {blogOwner?.avatar ? (
                  <img
                    src={blogOwner.avatar}
                    alt={blogOwner.nickname}
                    className={styles.avatarImage}
                  />
                ) : (
                  blogOwner?.nickname?.charAt(0) || "U"
                )}
              </div>
              <div className={styles.userInfo}>
                <h1 className={styles.nickname}>
                  {blogOwner?.nickname || "사용자"}
                </h1>
                <p className={styles.bio}>개발과 일상을 기록하는 공간입니다.</p>
              </div>
            </div>
          </div>

          <div className={styles.categoryFilter}>
            {categories.map((category) => (
              <button
                key={category.id}
                className={`${styles.categoryButton} ${
                  selectedCategory === category.id ? styles.active : ""
                }`}
                onClick={() => handleCategorySelect(category.id)}
              >
                {category.name}
              </button>
            ))}
          </div>

          <div className={styles.postsSection}>
            <h2 className={styles.sectionTitle}>
              {blogOwner?.nickname || "사용자"}님의 게시글
            </h2>
            <PostList 
              posts={pagedPostsData || {}} 
              loading={loading} 
              currentPage={currentPage}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserBlogPage;
